/**
 * client-panel-state.ts — серверные UX-отметки пути юзера в панели (туториал).
 *
 * Источник истины — сервер (эндпоинты mobile-gw /v1/panel/state, /v1/panel/tutorial-complete),
 * localStorage лишь быстрый кэш (без вспышки туториала до ответа сети). Мотив: два края панели
 * (panel.padla.pro / ru.panel.padla.pro) — localStorage привязан к домену+устройству, а сервер один.
 * Спека: docs/superpowers/specs/2026-07-08-panel-tutorial-server-flag-design.md.
 */

import { authHeaders, type PanelAuth } from '../lib/panel-auth';

/** GET серверной отметки. null → сеть/сервер недоступны (решаем по localStorage-фолбэку). */
export async function fetchTutorialCompleted(
  apiBase: string,
  token: PanelAuth | null | undefined
): Promise<boolean | null> {
  if (!token) return null;
  try {
    const resp = await fetch(`${apiBase}/v1/panel/state`, {
      headers: authHeaders(token),
    });
    if (!resp.ok) return null;
    const data = (await resp.json()) as { tutorial_completed?: boolean };
    return data.tutorial_completed === true;
  } catch {
    return null; // сеть/РКН → фолбэк на localStorage (антихрупкость)
  }
}

/** POST «туториал просмотрен» (fire-and-forget, idempotent на сервере). */
export function postTutorialComplete(apiBase: string, token: PanelAuth | null | undefined): void {
  if (!token) return;
  try {
    void fetch(`${apiBase}/v1/panel/tutorial-complete`, {
      method: 'POST',
      headers: authHeaders(token),
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* сеть/РКН — не мешаем UI */
  }
}

export interface TutorialVisibility {
  /** Показать модалку туториала. */
  show: boolean;
  /** Само-миграция: localStorage знает «видел», сервер ещё нет → отправить POST. */
  migrate: boolean;
}

/**
 * Чистое решение показа туториала. Сервер — истина; localStorage — кэш/фолбэк.
 * @param serverCompleted true/false с сервера, либо null если сервер недоступен.
 * @param localCompleted есть ли localStorage-отметка (быстрый кэш прежних сессий/устройства).
 */
export function resolveTutorialVisibility(args: {
  serverCompleted: boolean | null;
  localCompleted: boolean;
}): TutorialVisibility {
  const { serverCompleted, localCompleted } = args;
  if (serverCompleted === true) return { show: false, migrate: false };
  if (serverCompleted === false) {
    // Сервер не знает. Если localStorage знает — видел на этом устройстве/домене → не показываем,
    // но мигрируем отметку на сервер (П0/П2 — localStorage это доказательство, не догадка).
    return localCompleted ? { show: false, migrate: true } : { show: true, migrate: false };
  }
  // serverCompleted === null → сервер недоступен: решаем по localStorage-кэшу (фолбэк, П5).
  return { show: !localCompleted, migrate: false };
}
